import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
  LineChart, Line
} from "recharts";
import { 
  getOwnerConsolidatedReport 
} from "@/lib/api";
import { 
  getCurrentOwner, 
  getOwnerShops 
} from "@/lib/auth";
import { formatCurrency, getCurrencyName } from "@/lib/currency";
import { 
  TrendingUp, 
  DollarSign, 
  ShoppingCart, 
  Store, 
  Package,
  AlertTriangle,
  BarChart3,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Clock,
  CreditCard,
  TrendingDown
} from "lucide-react";

// Colores para gráficos
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
const HEATMAP_COLORS = ['#f0f9ff', '#bae6fd', '#7dd3fc', '#38bdf8', '#0284c7', '#075985'];

export default function OwnerReports() {
  const owner = getCurrentOwner();
  const shops = getOwnerShops();
  const queryClient = useQueryClient(); // ← Agrega esta línea
  
  // Estado para filtros
  const [filters, setFilters] = useState(() => {
    // Fechas por defecto: últimos 30 días
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      shopIds: [] as string[],
      activePreset: 'last30Days' as 'today' | 'thisWeek' | 'thisMonth' | 'last30Days' | 'custom',
    };
  });

  // Estado para forzar refresco de datos
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Función para aplicar presets de fecha (USANDO FECHA LOCAL para cálculos)
const applyDatePreset = (preset: 'today' | 'thisWeek' | 'thisMonth' | 'last30Days') => {
  const now = new Date(); // Fecha LOCAL del usuario
  
  // Crear fecha de hoy en LOCAL (sin horas/minutos/segundos)
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  let startDate = new Date(todayLocal);
  
  switch (preset) {
    case 'today':
      // Hoy (fecha local)
      startDate = new Date(todayLocal);
      break;
    
    case 'thisWeek':
      // Esta semana (desde lunes) en fecha local
      const dayOfWeek = todayLocal.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate.setDate(todayLocal.getDate() - diffToMonday);
      break;
    
    case 'thisMonth':
      // Este mes en fecha local (desde día 1)
      startDate = new Date(todayLocal.getFullYear(), todayLocal.getMonth(), 1);
      break;
    
    case 'last30Days':
      // Últimos 30 días desde fecha local
      startDate.setDate(todayLocal.getDate() - 30);
      break;
  }
  
  // EndDate debe ser hoy (fin del día) en fecha local
  const endDate = new Date(todayLocal);
  endDate.setHours(23, 59, 59, 999);
  
  // Convertir a strings YYYY-MM-DD para mostrar en UI (fechas locales)
  const formatDateForUI = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  setFilters(prev => ({
    ...prev,
    startDate: formatDateForUI(startDate), // Mostrar fecha LOCAL en UI
    endDate: formatDateForUI(endDate),     // Mostrar fecha LOCAL en UI
    activePreset: preset
  }));
};
  
  // Fetch reporte consolidado
const { data: report, isLoading, error, refetch } = useQuery({
  queryKey: ["owner-reports", owner?.id, filters, refreshKey],
  queryFn: () => {
    // Crear fechas UTC directamente desde los strings YYYY-MM-DD
    const [startYear, startMonth, startDay] = filters.startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = filters.endDate.split('-').map(Number);
    
    // Inicio del día en UTC
    const startDateObj = new Date(Date.UTC(startYear, startMonth - 1, startDay, 0, 0, 0, 0));
    
    // Fin del día en UTC
    const endDateObj = new Date(Date.UTC(endYear, endMonth - 1, endDay, 23, 59, 59, 999));
    
    return getOwnerConsolidatedReport(owner!.id, {
      startDate: startDateObj.toISOString(),
      endDate: endDateObj.toISOString(),
      shopIds: filters.shopIds.length > 0 ? filters.shopIds : undefined,
    });
  },
  enabled: !!owner,
  refetchInterval: 30000,
  refetchIntervalInBackground: true,
});

  // Logs cuando el reporte llega
  useEffect(() => {
    if (report) {
      console.log("✅ [API RESPONSE] Reporte recibido correctamente");
      console.log("📊 Ventas totales:", report.summary?.totalSales);
      console.log("🏪 Tiendas en reporte:", report.shops?.length);
      console.log("📅 Período:", report.period);
    }
  }, [report]);

  // Logs cuando hay error
  useEffect(() => {
    if (error) {
      console.error("❌ [API ERROR] Error al obtener reporte:", error);
    }
  }, [error]);

  // Manejar cambios en filtros
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const handleShopSelection = (shopId: string) => {
    const newShopIds = filters.shopIds.includes(shopId)
      ? filters.shopIds.filter(id => id !== shopId)
      : [...filters.shopIds, shopId];
    handleFilterChange('shopIds', newShopIds);
  };
  
  const selectAllShops = () => {
    handleFilterChange('shopIds', shops.map(shop => shop.id));
  };
  
  const clearShopsFilter = () => {
    handleFilterChange('shopIds', []);
  };
  
  // Formatear datos para gráficos
  const shopSalesData = report?.shops.map(shop => ({
    name: shop.shopName.length > 12 ? shop.shopName.substring(0, 10) + '...' : shop.shopName,
    nameFull: shop.shopName,
    ventas: Number(shop.totalRevenue),
    transacciones: shop.salesCount,
    ticketPromedio: shop.averageTicket,
  })) || [];

  const paymentMethodData = report?.shops.flatMap(shop => [
    { name: `${shop.shopName} - Efectivo`, value: shop.paymentMethodBreakdown.efectivo, type: 'efectivo' },
    { name: `${shop.shopName} - Transferencia`, value: shop.paymentMethodBreakdown.transferencia, type: 'transferencia' },
  ]) || [];
  
  const totalPaymentMethods = {
    efectivo: report?.shops.reduce((sum, shop) => sum + shop.paymentMethodBreakdown.efectivo, 0) || 0,
    transferencia: report?.shops.reduce((sum, shop) => sum + shop.paymentMethodBreakdown.transferencia, 0) || 0,
  };
  
  // Preparar datos para heatmap
  const heatmapData = report?.hourlyHeatmap
    .filter(item => item.salesCount > 0)
    .map(item => ({
      hour: item.hour,
      day: item.dayOfWeek,
      value: item.salesCount,
      revenue: item.revenue,
    })) || [];
 
  // Días de la semana para heatmap
  const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  if (!owner) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">Acceso no autorizado</h3>
            <p className="text-muted-foreground">Debes iniciar sesión como dueño para ver los reportes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
    // Función para formatear ticks del eje Y
    const formatYAxisTick = (value: number) => {
      const symbol = owner?.currency === 'USD' ? '$' : 
                     owner?.currency === 'EUR' ? '€' : 
                     owner?.currency === 'L' ? 'L' : '$';
      
      // Formato simplificado para ticks
      if (value >= 1000000) return `${symbol}${(value/1000000).toFixed(0)}M`;
      if (value >= 1000) return `${symbol}${(value/1000).toFixed(0)}K`;
      return `${symbol}${value}`;
    };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header con filtros */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <BarChart3 className="h-6 w-6" />
                Reportes Consolidados
              </CardTitle>
              <CardDescription>
                Análisis unificado de todas tus tiendas • {owner.businessName}
                {owner.currency && ` • Moneda: ${getCurrencyName(owner.currency)}`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
            <Button 
  variant="outline" 
  size="sm" 
  onClick={async () => {
    // 1. Invalidar cache de React Query
    await queryClient.invalidateQueries({ 
      queryKey: ["owner-reports", owner?.id] 
    });
    
    // 2. Forzar refetch inmediato
    await refetch();
    
    // 3. Incrementar refreshKey también para buena medida
    setRefreshKey(prev => prev + 1);
    
    // Mostrar feedback al usuario
    toast({
      title: "Actualizando reportes",
      description: "Obteniendo los datos más recientes...",
      duration: 2000,
    });
  }}
  disabled={isLoading}
>
  <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
  Actualizar
</Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtro de fechas con presets */}
            <div className="space-y-2">
              <Label>Período</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => applyDatePreset('today')}
                  className={filters.activePreset === 'today' ? 'bg-primary text-primary-foreground' : ''}
                >
                  Hoy
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => applyDatePreset('thisWeek')}
                  className={filters.activePreset === 'thisWeek' ? 'bg-primary text-primary-foreground' : ''}
                >
                  Esta Semana
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => applyDatePreset('thisMonth')}
                  className={filters.activePreset === 'thisMonth' ? 'bg-primary text-primary-foreground' : ''}
                >
                  Este Mes
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => applyDatePreset('last30Days')}
                  className={filters.activePreset === 'last30Days' ? 'bg-primary text-primary-foreground' : ''}
                >
                  Últimos 30 días
                </Button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Desde</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => {
                      handleFilterChange('startDate', e.target.value);
                      setFilters(prev => ({ ...prev, activePreset: 'custom' }));
                    }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="endDate">Hasta</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => {
                      handleFilterChange('endDate', e.target.value);
                      setFilters(prev => ({ ...prev, activePreset: 'custom' }));
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Filtro de tiendas */}
            <div className="space-y-2">
              <Label>Filtrar por Tiendas</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllShops}>
                  Todas
                </Button>
                <Button variant="outline" size="sm" onClick={clearShopsFilter}>
                  Ninguna
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {shops.map(shop => (
                  <Badge
                    key={shop.id}
                    variant={filters.shopIds.includes(shop.id) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => handleShopSelection(shop.id)}
                  >
                    {shop.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {isLoading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 mx-auto animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Generando reportes consolidados...</p>
        </div>
      ) : error ? (
        <Card className="border-destructive/50">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h3 className="text-lg font-medium mb-2">Error al cargar reportes</h3>
            <p className="text-muted-foreground">{error.message}</p>
            <Button onClick={() => refetch()} className="mt-4">
              Reintentar
            </Button>
          </CardContent>
        </Card>
      ) : report && (
        <>
          {/* Resumen ejecutivo */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Ingresos Totales</p>
                    <h3 className="text-2xl font-bold">
                      {formatCurrency(report.summary.totalRevenue, owner?.currency || 'CUP')}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {report.summary.totalSales} transacciones
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Ticket Promedio</p>
                    <h3 className="text-2xl font-bold">
                      {formatCurrency(report.summary.averageTicket, owner?.currency || 'CUP')}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Por transacción
                    </p>
                  </div>
                  <CreditCard className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Tiendas Activas</p>
                    <h3 className="text-2xl font-bold">{report.summary.activeShops}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {filters.shopIds.length > 0 ? `${filters.shopIds.length} filtradas` : 'Todas'}
                    </p>
                  </div>
                  <Store className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Período Analizado</p>
                    <h3 className="text-lg font-bold">
                      {new Date(report.period.start).toLocaleDateString()} - {new Date(report.period.end).toLocaleDateString()}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {filters.activePreset === 'today' ? 'Hoy' : 
                       filters.activePreset === 'thisWeek' ? 'Esta semana' :
                       filters.activePreset === 'thisMonth' ? 'Este mes' :
                       filters.activePreset === 'last30Days' ? 'Últimos 30 días' : 'Período personalizado'}
                    </p>
                  </div>
                  <Calendar className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Tabs principales */}
          <Tabs defaultValue="sales" className="space-y-6">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 justify-center">
              <TabsTrigger value="sales" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Ventas
              </TabsTrigger>
              <TabsTrigger value="heatmap" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Horario
              </TabsTrigger>
              <TabsTrigger value="products" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Productos
              </TabsTrigger>
              <TabsTrigger value="alerts" className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Alertas
              </TabsTrigger>
            </TabsList>
            
            {/* Tab: Ventas por Tienda */}
            <TabsContent value="sales" className="space-y-6">
              <Card>
                <CardHeader className="text-center">
                  <CardTitle>Ventas por Tienda</CardTitle>
                  <CardDescription>
                    Comparativa de ingresos entre tus puntos de venta
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Vista Desktop - Barras verticales */}
                  <div className="hidden md:block h-80 min-h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={shopSalesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          angle={-30}
                          textAnchor="end"
                          height={50}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis />
                        <Tooltip 
                          formatter={(value) => [formatCurrency(Number(value), owner?.currency || 'CUP'), 'Ingresos']}
                          labelFormatter={(label) => {
                            const shop = shopSalesData.find(s => s.name === label);
                            return `Tienda: ${shop?.nameFull || label}`;
                          }}
                        />
                        <Legend />
                        <Bar dataKey="ventas" name="Ingresos ($)" fill="#0088FE" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Vista Móvil - Gráfico de líneas */}
                  <div className="md:hidden h-[350px] min-h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={shopSalesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          tickFormatter={formatYAxisTick}
                        />
                        <Tooltip 
                          formatter={(value) => [formatCurrency(Number(value), owner?.currency || 'CUP'), 'Ingresos']}
                          labelFormatter={(label) => {
                            const shop = shopSalesData.find(s => s.name === label);
                            return `Tienda: ${shop?.nameFull || label}`;
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="ventas" 
                          stroke="#0088FE" 
                          name="Ingresos ($)" 
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Métodos de Pago */}
                <Card>
                  <CardHeader className="text-center">
                    <CardTitle>Métodos de Pago</CardTitle>
                    <CardDescription>
                      Distribución por tipo de pago
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Gráfico Pie - Solo en desktop */}
                    <div className="hidden md:block h-64 min-h-[256px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Efectivo', value: totalPaymentMethods.efectivo },
                              { name: 'Transferencia', value: totalPaymentMethods.transferencia },
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#0088FE" />
                            <Cell fill="#00C49F" />
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Tarjetas de estadísticas */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-4 mt-4 md:mt-0">
                      <div className="text-center p-4 sm:p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-700">Efectivo</p>
                        <p className="text-2xl font-bold">{totalPaymentMethods.efectivo}</p>
                        <p className="text-xs text-blue-600">transacciones</p>
                      </div>
                      <div className="text-center p-4 sm:p-3 bg-green-50 rounded-lg">
                        <p className="text-sm font-medium text-green-700">Transferencia</p>
                        <p className="text-2xl font-bold">{totalPaymentMethods.transferencia}</p>
                        <p className="text-xs text-green-600">transacciones</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Tabla detallada de tiendas */}
                <Card>
                  <CardHeader className="text-center">
                    <CardTitle>Detalle por Tienda</CardTitle>
                    <CardDescription>
                      Métricas individuales de cada punto de venta
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tienda</TableHead>
                            <TableHead className="text-right">Ingresos</TableHead>
                            <TableHead className="text-right">Transacciones</TableHead>
                            <TableHead className="text-right">Ticket Prom.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {report.shops.map((shop) => (
                            <TableRow key={shop.shopId}>
                              <TableCell className="font-medium">{shop.shopName}</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(shop.totalRevenue, owner?.currency || 'CUP')}
                              </TableCell>
                              <TableCell className="text-right">{shop.salesCount}</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(shop.averageTicket, owner?.currency || 'CUP')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            {/* Tab: Heatmap de Ventas */}
            <TabsContent value="heatmap">
              <Card>
                <CardHeader className="text-center">
                  <CardTitle>Heatmap de Ventas</CardTitle>
                  <CardDescription>
                    Distribución de ventas por hora y día de la semana
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {heatmapData.length > 0 ? (
                    <div className="w-full overflow-x-auto">
                      <div className="min-w-[600px] mx-auto">
                        {/* Encabezado: Horas (0-23) en eje X */}
                        <div className="flex justify-center" style={{ marginLeft: '3rem' }}>
                          {Array.from({ length: 24 }, (_, hour) => (
                            <div key={hour} className="w-6 sm:w-8 text-center text-xs text-muted-foreground">
                              {hour}h
                            </div>
                          ))}
                        </div>
                        
                        {/* Filas: Días de la semana (0=Dom a 6=Sáb) */}
                        {daysOfWeek.map((day, dayIndex) => (
                          <div key={dayIndex} className="flex items-center justify-center mt-1">
                            {/* Etiqueta del día */}
                            <div className="w-12 text-sm font-medium text-right pr-2">
                              {day}
                            </div>
                            
                            {/* Celdas de horas para este día */}
                            <div className="flex gap-0.5 sm:gap-1">
                              {Array.from({ length: 24 }, (_, hour) => {
                                const cellData = heatmapData.find(d => d.day === dayIndex && d.hour === hour);
                                const value = cellData?.value || 0;
                                const revenue = cellData?.revenue || 0;
                                
                                let intensity = 0;
                                if (value > 0) intensity = 1;
                                if (value >= 2) intensity = 2;
                                if (value >= 4) intensity = 3;
                                if (value >= 6) intensity = 4;
                                if (value >= 8) intensity = 5;
                                
                                return (
                                  <div
                                    key={`${dayIndex}-${hour}`}
                                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-sm flex items-center justify-center text-[10px] sm:text-xs"
                                    style={{
                                      backgroundColor: HEATMAP_COLORS[intensity],
                                      border: '1px solid #e5e7eb',
                                    }}
                                    title={`${day} ${hour}:00 - ${hour}:59\n${value} venta${value !== 1 ? 's' : ''}\n${formatCurrency(revenue, owner?.currency || 'CUP')} ingresos`}
                                  >
                                    {value > 0 ? value : ''}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        
                        {/* Leyenda */}
                        <div className="flex items-center justify-center mt-4 sm:mt-6 gap-2 sm:gap-4 flex-wrap">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-sm" style={{ backgroundColor: HEATMAP_COLORS[0] }} />
                            <span className="text-[10px] sm:text-xs">0 ventas</span>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-sm" style={{ backgroundColor: HEATMAP_COLORS[1] }} />
                            <span className="text-[10px] sm:text-xs">1 venta</span>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-sm" style={{ backgroundColor: HEATMAP_COLORS[2] }} />
                            <span className="text-[10px] sm:text-xs">2-3 ventas</span>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-sm" style={{ backgroundColor: HEATMAP_COLORS[4] }} />
                            <span className="text-[10px] sm:text-xs">6+ ventas</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No hay datos de ventas para el período seleccionado</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Tab: Productos Más Vendidos */}
            <TabsContent value="products">
              <Card>
                <CardHeader className="text-center">
                  <CardTitle>Productos Más Vendidos</CardTitle>
                  <CardDescription>
                    Top 10 productos con mayor rotación
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {report.topProducts.length > 0 ? (
                    <div className="space-y-6">
                      <div className="h-64 min-h-[256px] w-full overflow-x-auto">
                        <div className="min-w-[250px] h-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={report.topProducts}
                              layout="vertical"
                              margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                              <XAxis 
                                type="number" 
                                tick={{ fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis 
                                type="category" 
                                dataKey="productName" 
                                tick={{ fontSize: 10 }}
                                width={60}
                                axisLine={false}
                                tickLine={false}
                              />
                              <Tooltip 
                                formatter={(value) => [value, 'Unidades vendidas']}
                              />
                              <Legend />
                              <Bar 
                                dataKey="totalSold" 
                                name="Unidades Vendidas" 
                                fill="#8884d8" 
                                radius={[0, 4, 4, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Producto</TableHead>
                              <TableHead>Categoría</TableHead>
                              <TableHead className="text-right">Unidades Vendidas</TableHead>
                              <TableHead className="text-right">Ingresos</TableHead>
                              <TableHead className="text-right">Tiendas</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {report.topProducts.map((product) => (
                              <TableRow key={product.productId}>
                                <TableCell className="font-medium">{product.productName}</TableCell>
                                <TableCell>{product.category}</TableCell>
                                <TableCell className="text-right">{product.totalSold}</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(product.totalRevenue, owner?.currency || 'CUP')}
                                </TableCell>
                                <TableCell className="text-right">{product.shopsSelling}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No hay productos vendidos en el período seleccionado</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Tab: Alertas de Stock */}
            <TabsContent value="alerts">
              <Card>
                <CardHeader className="text-center">
                  <CardTitle>Alertas de Stock Bajo</CardTitle>
                  <CardDescription>
                    Productos que necesitan reposición en tus tiendas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {report.lowStockAlerts.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {report.lowStockAlerts.map((alert, index) => (
                          <Card key={index} className="border-amber-200">
                            <CardContent className="pt-6">
                              <div className="flex items-start justify-between">
                                <div>
                                  <Badge variant="outline" className="mb-2 bg-amber-50 text-amber-700">
                                    {alert.shopName}
                                  </Badge>
                                  <h4 className="font-medium">{alert.productName}</h4>
                                  <div className="flex items-center gap-4 mt-2">
                                    <div>
                                      <p className="text-xs text-muted-foreground">Stock Actual</p>
                                      <p className={`text-lg font-bold ${alert.currentStock === 0 ? 'text-destructive' : 'text-amber-600'}`}>
                                        {alert.currentStock} unidades
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">Límite</p>
                                      <p className="text-lg font-bold text-gray-600">
                                        {alert.threshold} unidades
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <AlertTriangle className="h-6 w-6 text-amber-500" />
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                      
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                          <h4 className="font-medium text-amber-800">Acción Recomendada</h4>
                        </div>
                        <p className="text-sm text-amber-700 mt-2">
                          {report.lowStockAlerts.length} producto{report.lowStockAlerts.length !== 1 ? 's' : ''} necesita{report.lowStockAlerts.length === 1 ? '' : 'n'} reposición inmediata para evitar desabastecimiento.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <AlertTriangle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                      <p className="text-muted-foreground">¡Excelente! No hay alertas de stock bajo en este momento.</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Todos los productos tienen stock por encima del límite mínimo.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}